import React, { useState } from 'react';
import { StyleSheet, View } from 'react-native';
import { format } from 'date-fns';
import { useRecoilState } from 'recoil';

import { Theme } from '@constants';
import { useContextNodeHandle, useGetUserEmail } from '@hooks';
import { hasResetRefetchIntervalState } from '@store';

import { DebugPicker, DebugPickerValues } from './DebugPicker';

import Manifest from '../../../../manifest.json';
import { isAndroid, isFireTV, isGoogleTV, isTizen, isX1, platformName } from '../../../helpers/Platform';
import { TimeUtils } from '../../../helpers/TimeUtils';
import { TWCButton } from '../../focusable/TWCButton/TWCButton';
import { TWCPage } from '../../primitives/TWCPage/TWCPage';
import { TWCText } from '../../primitives/TWCText/TWCText';

export const DeviceDebugPage = ({ route }) => {
  const { debugItem } = route.params;

  return (
    <TWCPage
      childIds={[
        'datePickerYear',
        'datePickerMonth',
        'datePickerDay',
        'datePickerHour',
        'datePickerMinute',
        'datePickerAMPM',
        'datePickerSubmit',
        'datePickerClear',
      ]}
      firstFocus={'datePickerDay'}
      pageID={debugItem.pageID}
    >
      <DeviceDebugContent />
    </TWCPage>
  );
};

const versionString = Manifest?.branch_name ? `${Manifest.branch_name} - ${Manifest.build_number}` : null;

const DeviceDebugContent = () => {
  const email = useGetUserEmail();

  return (
    <View style={styles.pageWrapper}>
      <View style={styles.center}>
        <TWCText noTestID style={styles.title} variant={'pageHeader'}>
          {'Device info/settings'}
        </TWCText>
      </View>
      <View style={styles.pageContent}>
        <View style={styles.sideContent}>
          <TWCText noTestID variant={'medium'}>
            {'Device Info:'}
          </TWCText>
          <TWCText noTestID variant={'regularText30'}>{`Platform Name: ${platformName() ?? 'browser'}`}</TWCText>
          <TWCText noTestID variant={'regularText30'}>
            {'\nPlatform Flags:\n'}
          </TWCText>
          <TWCText
            noTestID
            variant={'regularText30'}
          >{`isFireTV: ${isFireTV} \nisGoogleTV: ${isGoogleTV} \nisTizen: ${isTizen} \nisAndroid: ${isAndroid} \nisX1: ${isX1}\n\n`}</TWCText>
          <TWCText noTestID variant={'regularText30'}>{`Device email: ${email ?? 'none set'}`}</TWCText>
          <TWCText noTestID variant={'regularText30'}>{`App version: ${versionString ?? 'none set'}`}</TWCText>
          <TWCText noTestID variant={'regularText30'}>{`Time zone: ${TimeUtils.systemTimeZone()}`}</TWCText>
        </View>
        <View style={styles.sideContent}>
          <TWCText noTestID variant={'medium'}>
            {'Set debug date:'}
          </TWCText>
          <DebugDatePicker />
        </View>
      </View>
    </View>
  );
};

const DebugDatePicker = () => {
  const currentDebugDate = Date.prototype.debugDate;
  const initDate = currentDebugDate ?? new Date(Date.now());
  const [pickerValues, setPickerValues] = useState({
    date: initDate,
    day: format(initDate, 'd'),
    month: format(initDate, 'M'),
    year: format(initDate, 'y'),
    hour: format(initDate, 'h'),
    minute: format(initDate, 'mm'),
    ampm: format(initDate, 'aaa'),
  });
  const dateString = currentDebugDate ? format(currentDebugDate, 'EEEE M/d/y, h:mm aaa O') : 'none set';
  const dayHandle = useContextNodeHandle('datePickerDay');
  const monthHandle = useContextNodeHandle('datePickerMonth');
  const yearHandle = useContextNodeHandle('datePickerYear');
  const hourHandle = useContextNodeHandle('datePickerHour');
  const minuteHandle = useContextNodeHandle('datePickerMinute');
  const ampmHandle = useContextNodeHandle('datePickerAMPM');
  const submitHandle = useContextNodeHandle('datePickerSubmit');
  const clearHandle = useContextNodeHandle('datePickerClear');

  const [hasResetRefetchInterval, setHasResetRefetchInterval] = useRecoilState(hasResetRefetchIntervalState);

  const resetRecoilRefetchState = () => {
    const defaultRecoilState = Object.fromEntries(
      Object.entries(hasResetRefetchInterval).map(([key, _]) => [key, false]),
    );

    setHasResetRefetchInterval(defaultRecoilState);
  };

  return (
    <>
      <TWCText noTestID variant={'regularText30'}>{`Current set date: ${dateString}`}</TWCText>

      <View style={styles.datePartWrapper}>
        <TWCText noTestID variant={'regularText30'}>
          {'Day:'}
        </TWCText>
        <DebugPicker
          initialValue={format(pickerValues.date, 'd')}
          nextFocusDown={hourHandle}
          nextFocusRight={monthHandle}
          testID={'datePickerDay'}
          values={[DebugPickerValues.days]}
          onValueChanged={value => {
            setPickerValues({
              ...pickerValues,
              day: value,
            });
          }}
        />
        <TWCText noTestID style={styles.spacing} variant={'regularText30'}>
          {'Month:'}
        </TWCText>
        <DebugPicker
          initialValue={format(pickerValues.date, 'M')}
          nextFocusDown={hourHandle}
          nextFocusLeft={dayHandle}
          nextFocusRight={yearHandle}
          testID={'datePickerMonth'}
          values={[DebugPickerValues.month]}
          onValueChanged={value => {
            setPickerValues({
              ...pickerValues,
              month: value,
            });
          }}
        />
        <TWCText noTestID style={styles.spacing} variant={'regularText30'}>
          {'Year:'}
        </TWCText>
        <DebugPicker
          initialValue={format(pickerValues.date, 'y')}
          nextFocusDown={hourHandle}
          nextFocusLeft={monthHandle}
          testID={'datePickerYear'}
          values={[['1', '2', '3'], DebugPickerValues.decimal, DebugPickerValues.decimal, DebugPickerValues.decimal]}
          onValueChanged={value => {
            setPickerValues({
              ...pickerValues,
              year: value,
            });
          }}
        />
      </View>
      <View style={styles.datePartWrapper}>
        <TWCText noTestID variant={'regularText30'}>
          {'Time:'}
        </TWCText>
        <DebugPicker
          initialValue={format(pickerValues.date, 'h')}
          nextFocusDown={submitHandle}
          nextFocusRight={minuteHandle}
          nextFocusUp={dayHandle}
          testID={'datePickerHour'}
          values={[DebugPickerValues.month]}
          onValueChanged={value => {
            setPickerValues({
              ...pickerValues,
              hour: value,
            });
          }}
        />
        <TWCText noTestID style={styles.smallSpacing} variant={'regularText30'}>
          {':'}
        </TWCText>
        <DebugPicker
          initialValue={format(pickerValues.date, 'mm')}
          nextFocusDown={submitHandle}
          nextFocusLeft={hourHandle}
          nextFocusRight={ampmHandle}
          nextFocusUp={dayHandle}
          testID={'datePickerMinute'}
          values={[['0', '1', '2', '3', '4', '5'], DebugPickerValues.decimal]}
          onValueChanged={value => {
            setPickerValues({
              ...pickerValues,
              minute: value,
            });
          }}
        />
        <DebugPicker
          initialValue={format(pickerValues.date, 'aaa')}
          nextFocusDown={submitHandle}
          nextFocusLeft={minuteHandle}
          nextFocusUp={dayHandle}
          testID={'datePickerAMPM'}
          values={[['am', 'pm']]}
          width={75}
          onValueChanged={value => {
            setPickerValues({
              ...pickerValues,
              ampm: value,
            });
          }}
        />
      </View>
      <View style={styles.buttonWrapper}>
        <TWCButton
          nextFocusRight={clearHandle}
          nextFocusUp={hourHandle}
          style={styles.datePartWrapper}
          testID={'datePickerSubmit'}
          title={'Set Date'}
          onPress={() => {
            resetRecoilRefetchState();
            const newDate = new Date();

            newDate.setFullYear(
              parseInt(pickerValues.year, 10),
              parseInt(pickerValues.month, 10) - 1,
              parseInt(pickerValues.day, 10),
            );
            let hours = parseInt(pickerValues.hour, 10);

            if (pickerValues.ampm === 'pm') {
              hours += 12;
            }
            newDate.setHours(hours, parseInt(pickerValues.minute, 10), 0);
            // eslint-disable-next-line no-extend-native
            Date.prototype.debugDate = newDate;
            setPickerValues({
              ...pickerValues,
              date: newDate,
            });
          }}
        />
        <TWCButton
          nextFocusLeft={submitHandle}
          nextFocusUp={hourHandle}
          style={styles.datePartWrapper}
          testID={'datePickerClear'}
          title={'Clear'}
          onPress={() => {
            resetRecoilRefetchState();
            // eslint-disable-next-line no-extend-native
            Date.prototype.debugDate = null;
            const now = new Date(Date.now());
            // TODO: figure out why this doesn't work

            setPickerValues({
              ...pickerValues,
              date: now,
              day: format(now, 'd'),
              month: format(now, 'M'),
              year: format(now, 'y'),
              hour: format(now, 'h'),
              minute: format(now, 'mm'),
              ampm: format(now, 'aaa'),
            });
          }}
        />
      </View>
    </>
  );
};

const styles = StyleSheet.create({
  buttonWrapper: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: Theme.px(10),
    width: Theme.px(825),
  },
  center: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  datePartWrapper: {
    alignItems: 'center',
    flexDirection: 'row',
    marginTop: Theme.px(10),
  },
  pageContent: {
    flexDirection: 'row',
  },
  pageWrapper: {
    flexDirection: 'column',
    justifyContent: 'center',
  },
  sideContent: {
    flexDirection: 'column',
    marginLeft: Theme.px(60),
    marginTop: Theme.px(40),
    width: Theme.appWidth / 2 - Theme.px(60),
  },
  smallSpacing: {
    marginLeft: Theme.px(10),
  },
  spacing: {
    marginLeft: Theme.px(20),
  },
  title: {
    marginTop: Theme.px(20),
  },
});
